import { CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import React, { useEffect, useState } from "react";
import type Stripe from "stripe";
import { z } from "zod";

import { toast } from "@performyard/py-components";

import Button from "../../../../components/Button";
import ContentModal from "../../../../components/Modal/Templates/ContentModal";

import { useAddOrUpdateCreditCard } from "../../../../api/hooks";
import useForm from "../../../../hooks/useForm";

import CardFormFields from "../CardFormFields";

const CardFormSchema = z.object({
	cardName: z.string().min(1, "Name on card is required"),
	cardZipCode: z.string().min(1, "Card zip code is required"),
});

interface CreditCardModalProps {
	isOpen: boolean;
	onClose: (canceled?: boolean) => void;
	setupIntent: Stripe.SetupIntent | null;
}

export interface FormErrors {
	cardName?: string;
	zipCode?: string;
}

const CreditCardModal = ({ isOpen, onClose, setupIntent }: CreditCardModalProps) => {
	const stripe = useStripe();
	const elements = useElements();
	const [validationErrors, setValidationErrors] = useState<FormErrors>({
		cardName: undefined,
		zipCode: undefined,
	});
	const [isLoading, setIsLoading] = useState(false);

	const {
		state: formState,
		updateForm,
		resetForm,
		validate,
	} = useForm({
		schema: CardFormSchema,
		defaultValues: {
			cardName: "",
			cardZipCode: "",
		},
	});

	// Only reset the form when the modal closes
	useEffect(() => {
		if (!isOpen) {
			resetForm();
			setValidationErrors({
				cardName: undefined,
				zipCode: undefined,
			});
		}
	}, [isOpen]);

	const { mutate: addOrUpdateCreditCard, isPending: isAddOrUpdateCardPending } = useAddOrUpdateCreditCard(onClose);

	const handleInputChange = (field: keyof typeof formState) => (val: string) => {
		updateForm({ [field]: val });
		setValidationErrors((prev) => ({
			...prev,
			[field === "cardName" ? "cardName" : "zipCode"]: undefined,
		}));
	};

	// Function for adding/updating card
	const handleSubmit = async () => {
		setValidationErrors({
			cardName: undefined,
			zipCode: undefined,
		});
		const validation = validate();
		if (!validation.isValid) {
			setValidationErrors({
				cardName: validation.errors?.cardName?.message,
				zipCode: validation.errors?.cardZipCode?.message,
			});
			return;
		}

		if (!stripe || !elements || !setupIntent?.client_secret) {
			return;
		}

		const cardElement = elements.getElement(CardNumberElement);
		if (!cardElement) return;

		setIsLoading(true);
		try {
			const result = await stripe.confirmCardSetup(setupIntent.client_secret, {
				payment_method: {
					card: cardElement,
					billing_details: {
						name: validation.data.cardName,
						address: {
							postal_code: validation.data.cardZipCode,
						},
					},
				},
			});

			if (result.error) {
				console.error("Error confirming card setup:", result.error);
				toast.error("Error processing credit card update");
				return;
			}

			if (result.setupIntent?.payment_method) {
				// Get the payment method ID, whether it's a string or PaymentMethod object
				const paymentMethodId =
					typeof result.setupIntent.payment_method === "string"
						? result.setupIntent.payment_method
						: result.setupIntent.payment_method.id;
				addOrUpdateCreditCard(paymentMethodId);
			}
		} catch (error) {
			console.error("Error processing card submission:", error);
		} finally {
			setIsLoading(false);
		}
	};

	if (!setupIntent) return null;

	return (
		<ContentModal
			size="large"
			title="Card Details"
			isOpen={isOpen}
			handleClose={() => onClose(true)}
			buttons={[
				<Button
					key="submit"
					color="blue"
					onClick={handleSubmit}
					disabled={!stripe}
					isRequesting={isAddOrUpdateCardPending || isLoading}
				>
					Submit
				</Button>,
			]}
		>
			<CardFormFields
				cardName={formState.cardName}
				setCardName={handleInputChange("cardName")}
				cardZipCode={formState.cardZipCode}
				setCardZipCode={handleInputChange("cardZipCode")}
				errors={validationErrors}
			/>
		</ContentModal>
	);
};

export default CreditCardModal;
